import React, { useCallback, useEffect, useState } from 'react';
import { fetchInspections, downloadInspectionCsv, Inspection } from '../../services/inspectionsApi';
import { btnSmall, inGroup, StatusBadge } from './common';
import { ui } from '../../styles/shared';
import { fmtDate, fmtDateTime, todayStr } from '../../utils/format';

/**
 * Fliken "Historik": gjorda kontroller och CSV-export för en period.
 * Laddar sin egen lista; laddar om när refreshKey ändras i föräldern.
 */
interface Props {
    group: string;                 // '' = alla grupper
    refreshKey: number;
    /** Öppnar en kontroll i panelen ovanför flikarna. */
    onOpen: (id: number) => Promise<void>;
    onError: (msg: string) => void;
}

const HistoryTab: React.FC<Props> = ({ group, refreshKey, onOpen, onError }) => {
    const [history, setHistory] = useState<Inspection[]>([]);
    const [exportFrom, setExportFrom] = useState(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toLocaleDateString('sv-SE'); });
    const [exportTo, setExportTo] = useState(todayStr());

    const load = useCallback(async () => {
        try { setHistory(await fetchInspections({ status: 'done' })); }
        catch (e: any) { onError(e.message); }
    }, [onError]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const exportCsv = async () => {
        try {
            const blob = await downloadInspectionCsv(exportFrom, exportTo);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `egenkontroller_${exportFrom}_${exportTo}.csv`; a.click();
            URL.revokeObjectURL(url);
        } catch (e: any) { onError('Kunde inte hämta exporten'); }
    };

    const gHistory = history.filter(i => inGroup(group, i.group));

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', fontSize: 13 }}>
                <span>Export:</span>
                <input type="date" value={exportFrom} onChange={e => setExportFrom(e.target.value)} style={{ padding: 6, border: '1px solid #ccc', borderRadius: 4 }} />
                <span>till</span>
                <input type="date" value={exportTo} onChange={e => setExportTo(e.target.value)} style={{ padding: 6, border: '1px solid #ccc', borderRadius: 4 }} />
                <button onClick={exportCsv} style={btnSmall('#0f3460')}>Ladda ner CSV</button>
            </div>
            {gHistory.length === 0 ? (
                <div style={{ ...ui.card, color: '#666' }}>Inga gjorda kontroller ännu.</div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
                        <thead><tr><th style={ui.th}>Senast</th><th style={ui.th}>Kontroll</th><th style={ui.th}>Plats</th><th style={ui.th}>Gjord</th><th style={ui.th}>Av</th><th style={ui.th}>Status</th><th style={ui.th}></th></tr></thead>
                        <tbody>
                            {gHistory.map(i => (
                                <tr key={i.id} style={{ background: i.has_deviation ? '#fff8e6' : undefined }}>
                                    <td style={ui.td}>{fmtDate(i.due_date)}</td>
                                    <td style={ui.td}><b>{i.schedule_name ?? i.template_name}</b><div style={{ fontSize: 12, color: '#777' }}>{i.template_name}</div></td>
                                    <td style={ui.td}>{i.zone_name ?? '–'}</td>
                                    <td style={ui.td}>{fmtDateTime(i.completed_at)}</td>
                                    <td style={ui.td}>{i.completed_by_name ?? '–'}</td>
                                    <td style={ui.td}><StatusBadge i={i} /></td>
                                    <td style={{ ...ui.td, textAlign: 'right' }}><button onClick={() => onOpen(i.id)} style={btnSmall('#e8f0fe', '#1a4aa6')}>Visa</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default HistoryTab;
